import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Beaker, FlaskConical, MessagesSquare, Package, FileText, Settings, LogOut, Eye, Sprout,
  Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/formulas", label: "Formulas", icon: Beaker, testid: "nav-formulas" },
  { to: "/ingredients", label: "Ingredients", icon: Sprout, testid: "nav-ingredients" },
  { to: "/experiments", label: "Experiments", icon: FlaskConical, testid: "nav-experiments" },
  { to: "/feedback", label: "Experience Centre", icon: MessagesSquare, testid: "nav-feedback" },
  { to: "/packaging", label: "Packaging & Costing", icon: Package, testid: "nav-packaging" },
  { to: "/documents", label: "Templates & SOPs", icon: FileText, testid: "nav-documents" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="app-layout">
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 bg-[#2A1810] text-[#FAF6F0] px-4 py-3 border-b border-[#3D2314]" data-testid="mobile-header">
        <button
          onClick={() => setNavOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-[#3D2314]"
          aria-label="Open navigation"
          data-testid="open-nav-button"
        >
          <Menu size={20} />
        </button>
        <div className="w-8 h-8 rounded-lg bg-[#C5A059] flex items-center justify-center font-display font-bold text-[#2A1810]">S</div>
        <span className="font-display text-base font-bold tracking-wide">Smoor R&D OS</span>
      </header>

      {navOpen && (
        <button
          className="lg:hidden fixed inset-0 bg-[#1C100B]/60 z-40"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
          data-testid="nav-backdrop"
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-screen w-64 bg-[#2A1810] text-[#EADCD0] flex flex-col border-r border-[#3D2314] z-50 transition-transform duration-200 lg:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-[#3D2314] flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#C5A059] flex items-center justify-center font-display font-bold text-[#2A1810] text-lg">S</div>
          <div>
            <div className="font-display text-lg font-bold text-[#FAF6F0] tracking-wide leading-tight">Smoor</div>
            <span className="bg-[#C5A059] text-[#2A1810] text-[10px] font-extrabold uppercase px-2 py-0.5 rounded">R&D OS</span>
          </div>
          <button
            onClick={() => setNavOpen(false)}
            className="lg:hidden ml-auto p-2 rounded-lg hover:bg-[#3D2314] text-[#EADCD0]"
            aria-label="Close navigation"
            data-testid="close-nav-button"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label: l, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 hover:bg-[#3D2314] hover:text-[#FAF6F0] ${
                  isActive ? "bg-[#3D2314] text-[#FAF6F0] border-l-4 border-[#C5A059] font-semibold" : ""
                }`
              }
            >
              <Icon size={17} strokeWidth={1.8} />
              {l}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#3D2314] bg-[#22130C] flex items-center justify-between" data-testid="sidebar-user">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#FAF6F0] truncate" data-testid="sidebar-user-name">{user?.full_name}</div>
            <div className="text-xs text-[#C5A059]" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            className="p-2 rounded-lg hover:bg-[#3D2314] text-[#EADCD0] hover:text-[#FAF6F0] transition-colors"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 lg:p-8 text-[#1C100B]">
        {user?.role === "leadership" && (
          <div className="mb-6 flex items-center gap-2 bg-[#F9F3E6] border border-[#E5C992] text-[#8A6D2F] rounded-lg px-4 py-2.5 text-sm font-medium" data-testid="readonly-banner">
            <Eye size={15} /> Read-only mode — Leadership access. Create, edit and delete actions are disabled.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
